import { Text } from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { retrieveContactQueryOptions } from "@/lib/data/react-query/contacts/contact";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  copyLinkToClipboard,
  copyToClipboard,
} from "@/lib/utils/copyToClipboard";
import {
  formatAddress,
  formatDate,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import {
  getPrimaryItem,
  getResolvedPrimaryEmail,
  getResolvedPrimaryPhone,
} from "@/lib/utils/matterContactConnectionHelpers";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BriefcaseBusiness,
  Cake,
  Copy,
  Hourglass,
  House,
  Languages,
  Mail,
  MapPin,
  Mars,
  NonBinary,
  Phone,
  UserRound,
  Venus,
  VenusAndMars,
  type LucideIcon,
} from "lucide-react";
import { getOverviewClientSummarySegments } from "../overview/matter-overview-utils";

function BannerDetail({
  icon: Icon,
  label,
  value,
  copyValue,
  copyable = true,
}: {
  icon?: LucideIcon;
  label: string;
  value?: string | null;
  copyValue?: string;
  copyable?: boolean;
}) {
  const isDateOfLoss = label === "Date of loss";
  const className = "flex shrink-0 items-center gap-1.5";
  const content = (
    <>
      {Icon ? (
        <Icon aria-hidden="true" className="text-foreground size-3.5" />
      ) : null}
      <Text
        as="span"
        variant="caption"
        className={Icon ? "sr-only" : "text-foreground font-semibold"}
      >
        {isDateOfLoss ? "DOL: " : `${label}:`}
      </Text>
      <Text
        as="span"
        variant="subHeading"
        className={cn(
          "text-foreground",
          (label === "Matter" || label === "Client") && "font-semibold",
        )}
      >
        {value || "—"}
      </Text>
    </>
  );

  if (!copyable) {
    return (
      <span
        role="group"
        aria-label={`${label}: ${value || "—"}`}
        title={`${label}: ${value || "—"}`}
        className={className}
      >
        {content}
      </span>
    );
  }

  return (
    <TooltipWrapper
      message={value ? `Copy ${label.toLowerCase()}` : `${label} not provided`}
    >
      <button
        type="button"
        aria-label={`Copy ${label.toLowerCase()}`}
        disabled={!value}
        className={cn(
          className,
          "focus-visible:ring-ring cursor-copy rounded-sm text-left transition-opacity hover:opacity-75 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-default disabled:hover:opacity-100",
        )}
        onClick={() => {
          void copyToClipboard(copyValue ?? value ?? "", label);
        }}
      >
        {content}
      </button>
    </TooltipWrapper>
  );
}

const detailIcons: Record<string, LucideIcon | undefined> = {
  Client: UserRound,
  Age: Hourglass,
  DOB: Cake,
  Language: Languages,
  Residence: House,
  Phone,
  Email: Mail,
  Address: MapPin,
};

const genderIcons: Record<string, LucideIcon | undefined> = {
  Male: Mars,
  Female: Venus,
  "Non-Binary": NonBinary,
};

export function MatterInformationBanner({ matter }: { matter: Matter }) {
  const clientConnection = getMatterContactConnections(matter).find(
    (connection) => connection.label === "Client",
  );
  const { data: clientData } = useQuery(
    retrieveContactQueryOptions(clientConnection?.contactId ?? ""),
  );
  const client = clientData?.data;
  const clientSummary = getOverviewClientSummarySegments(matter, client);
  const clientName = clientSummary.find(
    (segment) => segment.label === "Name",
  )?.value;
  const residence = clientSummary.find(
    (segment) => segment.label === "Residence",
  )?.value;
  const phone =
    getPrimaryItem(client?.phones ?? [], "isPrimary") ??
    getResolvedPrimaryPhone(matter, clientConnection);
  const email =
    getPrimaryItem(client?.emails ?? [], "isPrimary") ??
    getResolvedPrimaryEmail(matter, clientConnection);
  const address =
    getPrimaryItem(client?.addresses ?? [], "isPrimary") ??
    getPrimaryItem(clientConnection?.contact.addresses ?? [], "isPrimary");
  const formattedPhone = formatLocalOrIntlPhoneNumber(phone);
  const dateOfLoss = formatDate(matter.dateOfLoss);
  const details: {
    label: string;
    value?: string | null;
    copyValue?: string;
  }[] = [
    { label: "Client", value: clientName },
    ...["Gender", "Age", "DOB", "Language"].map((label) => ({
      label,
      value: clientSummary.find((segment) => segment.label === label)?.value,
    })),
    { label: "Residence", value: residence },
    { label: "Phone", value: formattedPhone, copyValue: phone?.number },
    { label: "Email", value: email?.address },
    {
      label: "Address",
      value: address
        ? formatAddress({
            ...address,
            country:
              address.country === "United States" ? undefined : address.country,
          })
        : undefined,
    },
    { label: "Date of loss", value: dateOfLoss },
  ];

  return (
    <section
      aria-label="Matter information"
      className="border-border min-w-0 border-b px-4 py-2"
    >
      <div
        role="group"
        aria-label="Matter summary"
        tabIndex={0}
        className="scrollbar-hidden focus-visible:ring-ring flex min-w-0 items-center gap-4 overflow-x-auto rounded-sm whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
      >
        <Link
          to="/matters/$matterId"
          params={{ matterId: matter.id }}
          className="focus-visible:ring-ring shrink-0 rounded-sm hover:underline focus-visible:ring-2 focus-visible:outline-none"
        >
          <BannerDetail
            icon={BriefcaseBusiness}
            label="Matter"
            value={matter.title}
            copyable={false}
          />
        </Link>
        <TooltipWrapper message="Copy matter link">
          <button
            type="button"
            aria-label="Copy matter link"
            className="text-foreground focus-visible:ring-ring -ml-2 shrink-0 cursor-copy rounded-sm hover:opacity-75 focus-visible:ring-2 focus-visible:outline-none"
            onClick={() => {
              void copyLinkToClipboard({
                label: "Matter link",
                text: matter.title,
                url: `${window.location.origin}/matters/${matter.id}`,
              });
            }}
          >
            <Copy aria-hidden="true" className="size-3.5" />
          </button>
        </TooltipWrapper>
        <span
          aria-hidden="true"
          className="border-border h-5 shrink-0 border-l"
        />
        {details.map(({ label, value, copyValue }) => (
          <BannerDetail
            key={label}
            icon={
              label === "Gender"
                ? (genderIcons[value ?? ""] ?? VenusAndMars)
                : detailIcons[label]
            }
            label={label}
            value={value}
            copyValue={copyValue}
          />
        ))}
      </div>
    </section>
  );
}
